const STORAGE_KEY = 'pokerapp-storage'

export const StorageKeys = {
  GAME_TOKEN: `${STORAGE_KEY}-game-token`,
  SETTINGS: `${STORAGE_KEY}-settings`,
  AUTH_TOKEN: `${STORAGE_KEY}-auth-token`,
  ROOM_ID: `${STORAGE_KEY}-room-id`,
  PASSED_TRAINING: `${STORAGE_KEY}-passed-training`,
  WELCOME_MODAL_CLOSED: `${STORAGE_KEY}-welcome-modal-closed`,
  PLAYER_OPENED_RESULTS: `${STORAGE_KEY}-player-opened-results`,
  VERSION: `${STORAGE_KEY}-version`
} as const

export type StorageKey = (typeof StorageKeys)[keyof typeof StorageKeys]

type Settings = {
  sound: boolean
  displayMode: 'bb' | 'chips'
  betLevels: number[]
}

export function saveToken(token: string) {
  const key = StorageKeys.GAME_TOKEN
  try {
    localStorage.setItem(key, token)
  } catch {
    // intentionally blank
  }
}

export function loadToken(): string | null {
  try {
    return localStorage.getItem(StorageKeys.GAME_TOKEN)
  } catch {
    return null
  }
}

export function removeGameToken() {
  const key = StorageKeys.GAME_TOKEN
  try {
    localStorage.removeItem(key)
  } catch {
    // intentionally blank
  }
}

export function loadPlayerSettings(): Settings {
  const defaultStorage: Settings = {
    sound: true,
    displayMode: 'chips',
    betLevels: [25, 50, 75, 100]
  }

  let storage: Settings | null = null
  try {
    storage = JSON.parse(localStorage.getItem(StorageKeys.SETTINGS)!)
  } catch {
    storage = defaultStorage
  }

  return storage ?? defaultStorage
}

export function savePlayerSettings(settings: Settings) {
  const storage = loadPlayerSettings()
  Object.assign(storage, settings)

  try {
    localStorage.setItem(StorageKeys.SETTINGS, JSON.stringify(storage))
  } catch {
    // intentionally blank
  }
}

export function saveAuthToken(token: string) {
  const key = StorageKeys.AUTH_TOKEN
  try {
    localStorage.setItem(key, token)
  } catch {
    // intentionally blank
  }
}

export function loadAuthToken(): string | null {
  try {
    return localStorage.getItem(StorageKeys.AUTH_TOKEN)
  } catch {
    return null
  }
}

export function removeAuthToken() {
  const key = StorageKeys.AUTH_TOKEN
  try {
    localStorage.removeItem(key)
  } catch {
    // intentionally blank
  }
}

export function saveRoomId(roomId: string) {
  const key = StorageKeys.ROOM_ID
  try {
    localStorage.setItem(key, roomId)
  } catch {
    // intentionally blank
  }
}

export function loadRoomId(): string | null {
  try {
    return localStorage.getItem(StorageKeys.ROOM_ID)
  } catch {
    return null
  }
}

export function removeRoomId() {
  const key = StorageKeys.ROOM_ID
  try {
    localStorage.removeItem(key)
  } catch {
    // intentionally blank
  }
}

export function setPassedTraining() {
  const key = StorageKeys.PASSED_TRAINING
  try {
    localStorage.setItem(key, '1')
  } catch {
    // intentionally blank
  }
}

export function loadPassedTraining(): string | null {
  try {
    return localStorage.getItem(StorageKeys.PASSED_TRAINING)
  } catch {
    return null
  }
}

// Welcome Modal
export function saveWelcomeModalClosed() {
  const key = StorageKeys.WELCOME_MODAL_CLOSED
  try {
    localStorage.setItem(key, 'true')
  } catch {
    // intentionally blank
  }
}

export function loadWelcomeModalClosed(): boolean {
  try {
    return localStorage.getItem(StorageKeys.WELCOME_MODAL_CLOSED) === 'true'
  } catch {
    return false
  }
}

export function removeWelcomeModalClosed() {
  const key = StorageKeys.WELCOME_MODAL_CLOSED
  try {
    localStorage.removeItem(key)
  } catch {
    // intentionally blank
  }
}

export function savePlayerOpenedResults() {
  try {
    localStorage.setItem(StorageKeys.PLAYER_OPENED_RESULTS, 'true')
  } catch {
    // intentionally blank
  }
}

export function loadPlayerOpenedResults(): boolean {
  try {
    return localStorage.getItem(StorageKeys.PLAYER_OPENED_RESULTS) === 'true'
  } catch {
    return false
  }
}

export function removePlayerOpenedResults() {
  try {
    localStorage.removeItem(StorageKeys.PLAYER_OPENED_RESULTS)
  } catch {
    // intentionally blank
  }
}

export function saveAppVersion(version: string) {
  try {
    localStorage.setItem(StorageKeys.VERSION, version)
  } catch {
    // intentionally blank
  }
}

export function loadAppVersion(): string | null {
  try {
    return localStorage.getItem(StorageKeys.VERSION)
  } catch {
    return null
  }
}
